import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, MenuItem, TextField, Alert, Typography,
} from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { superAdminAPI } from '../../services/api';
import { SuperAdminDataTable, apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { RADIUS } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

export const PendingApprovalsPage: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [planId, setPlanId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [t, p] = await Promise.all([superAdminAPI.getPendingTenants(), superAdminAPI.getPlans()]);
      setTenants(t.data);
      setPlans(p.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load pending tenants'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async () => {
    if (!selected || !planId) return;
    try {
      await superAdminAPI.assignPlan(selected._id, planId);
      setMessage('Tenant approved');
      setSelected(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Approval failed'));
    }
  };

  return (
    <Box>
      <PageHeader title="Pending Approvals" subtitle={getPageSubtitle('pending_approvals')}
        icon={<PendingActionsIcon />}
 />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <SuperAdminDataTable
        rows={tenants}
        loading={loading}
        error={error}
        exportFileName="super-admin-pending-tenants"
        exportSheetName="Pending Tenants"
        emptyMessage="No pending approvals"
        searchPlaceholder="Search pending companies..."
        statusOptions={[{ label: 'All', value: 'all' }]}
        columns={[
          {
            id: 'company',
            header: 'Company',
            searchValue: (t) => t.companyName,
            exportValue: (t) => t.companyName,
            render: (t) => <Typography fontWeight={600} fontSize={13}>{t.companyName}</Typography>,
          },
          {
            id: 'owner',
            header: 'Owner',
            searchValue: (t) => t.ownerName,
            exportValue: (t) => t.ownerName,
            render: (t) => t.ownerName,
          },
          {
            id: 'email',
            header: 'Email',
            searchValue: (t) => t.companyEmail,
            exportValue: (t) => t.companyEmail,
            render: (t) => t.companyEmail,
          },
          {
            id: 'date',
            header: 'Registered',
            searchValue: (t) => t.createdAt ? String(t.createdAt) : '',
            exportValue: (t) => t.createdAt,
            render: (t) => new Date(t.createdAt).toLocaleDateString(),
          },
        ]}
        renderActions={(t) => (
          <Button size="small" variant="contained" sx={catchyPrimaryButtonSx}
            onClick={() => { setSelected(t); setPlanId(''); }}>Approve</Button>
        )}
      />

      <Dialog open={!!selected} onClose={() => setSelected(null)} PaperProps={{ sx: { borderRadius: `${RADIUS}px`, p: 3, minWidth: 400 } }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Approve {selected?.companyName}</Typography>
        <TextField select label="Subscription Plan" fullWidth size="small" sx={{ ...catchyTextFieldSx, mb: 2 }}
          value={planId} onChange={(e) => setPlanId(e.target.value)}>
          {plans.filter((p) => p.status === 'active').map((p) => (
            <MenuItem key={p._id} value={p._id}>{p.planName} — ₹{p.monthlyPrice}/mo</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" fullWidth disabled={!planId} onClick={handleApprove} sx={catchyPrimaryButtonSx}>Approve & Activate</Button>
      </Dialog>
    </Box>
  );
};
